import React, { useState, useEffect, useRef } from 'react';
import MockArtistAutocomplete from '../components/MockArtistAutocomplete';
import ShareModal from '../components/ShareModal';
import { OptimalArtistSearch, SetlistData } from '../services/OptimalArtistSearch';
import { Artist } from '../services/ClientArtistSearch';
import { generatePlaylistHash, PlaylistCache, StoredPlaylist } from '../utils/playlistHash';
import { useAnalytics } from '../hooks/useAnalytics';
import { logger } from '../utils/logger';



// Extend window interface for playTrack function
declare global {
  interface Window {
    playTrack?: (index: number) => void;
  }
}



const BackgroundPattern: React.FC = () => (
  <div 
    className="fixed inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
    style={{
      backgroundImage: `url('/assets/abstract-background.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}
  />
);


// Legacy mock data - now handled by OptimalArtistSearch service

interface HomeProps {
  auth: {
    isAuthenticated: boolean;
    platform?: 'spotify';
    token?: string;
  };
  onLogout: () => void;
}

const Home: React.FC<HomeProps> = ({ auth, onLogout }) => {
  const [artistName, setArtistName] = useState('');
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const searchService = useRef(new OptimalArtistSearch());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab] = useState('artists');
  const { trackEvent, trackError, trackPageView, trackPlaylist, trackPlaylistCreationFailed } = useAnalytics();
  const [recentSearches, setRecentSearches] = useState<Artist[]>(() => {
    const saved = localStorage.getItem('recentSearches');
    return saved ? JSON.parse(saved) : [];
  });
  const [setlistData, setSetlistData] = useState<SetlistData | null>(null);
  const [loadingSetlist, setLoadingSetlist] = useState(false);
  const [addingToLibrary, setAddingToLibrary] = useState(false);
  const [playlistStatus, setPlaylistStatus] = useState<'not_created' | 'created'>('not_created');
  const [storedPlaylist, setStoredPlaylist] = useState<StoredPlaylist | null>(null);
  const [showConfirmationBanner, setShowConfirmationBanner] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const handleArtistSelect = async (artist: Artist) => {
    logger.log('🎯 Artist selected:', artist);
    
    // Track artist selection
    trackEvent({
      name: 'artist_selected',
      parameters: {
        artist_name: artist.name,
        artist_id: artist.mbid,
        search_type: 'manual'
      }
    });
    
    // Clear cache to ensure fresh setlist data
    logger.log('🧹 Clearing cache for fresh setlist data...');
    searchService.current.clearCache();
    
    // Set artist first (without artwork)
    setSelectedArtist(artist);
    setError('');
    setArtistName(''); // Clear the search field
    
    // Fetch artwork from Spotify backend
    try {
      logger.log('🎨 Fetching artwork for:', artist.name);
      const apiUrl = (window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.startsWith('192.168.'))
        ? 'http://localhost:3001' 
        : 'https://setlist-playlist-generator.onrender.com';
      
      const response = await fetch(`${apiUrl}/api/spotify/search/artists?q=${encodeURIComponent(artist.name)}`);
      
      if (response.ok) {
        const spotifyArtist = await response.json();
        logger.log('🎨 Spotify API response:', spotifyArtist);
        if (spotifyArtist.images && spotifyArtist.images.length > 0) {
          // Use the largest image (first one is usually the largest)
          const image = spotifyArtist.images[0];
          logger.log('🎨 Found Spotify artist image:', image);
          
          // Update artist with artwork
          const artistWithArtwork = {
            ...artist,
            artwork: {
              url: image.url,
              width: image.width,
              height: image.height
            }
          };
          logger.log('✅ Found artwork for:', artist.name, artistWithArtwork.artwork.url);
          setSelectedArtist(artistWithArtwork);
        } else {
          logger.log('⚠️ No images found in Spotify response for:', artist.name);
        }
      } else {
        logger.warn('⚠️ Spotify API request failed:', response.status);
      }
    } catch (error) {
      logger.warn('⚠️ Could not fetch artwork for:', artist.name, error);
      // Continue without artwork - not critical
    }
    
    // Add to recent searches
    const updated = [artist, ...recentSearches.filter(a => a.mbid !== artist.mbid)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };



  // Load setlist data when artist is selected
  useEffect(() => {
    if (selectedArtist) {
      const loadSetlist = async () => {
        try {
          setLoadingSetlist(true);
          logger.log('🎯 Loading setlist for:', selectedArtist);
          const data = await searchService.current.getArtistSetlists(selectedArtist);
          logger.log('🎶 Setlist data received:', data);
          setSetlistData(data);
        } catch (error) {
          logger.error('❌ Failed to load setlist data:', error);
          setError(`Failed to load setlist for ${selectedArtist.name}`);
        } finally {
          setLoadingSetlist(false);
        }
      };
      
      loadSetlist();
    } else {
      setSetlistData(null);
      setLoadingSetlist(false);
    }
  }, [selectedArtist]);

  // Check playlist status when setlist data changes
  useEffect(() => {
    if (selectedArtist && setlistData) {
      const hash = generatePlaylistHash(selectedArtist.mbid, setlistData);
      const cachedPlaylist = PlaylistCache.get(hash);
      
      if (cachedPlaylist) {
        setPlaylistStatus('created');
        setStoredPlaylist(cachedPlaylist);
        logger.log('🎵 Found existing playlist:', cachedPlaylist.playlistName);
      } else {
        setPlaylistStatus('not_created');
        setStoredPlaylist(null);
      }
    } else {
      setPlaylistStatus('not_created');
      setStoredPlaylist(null);
    }
  }, [selectedArtist, setlistData]);


  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);


  const handleAddToLibrary = async () => {
    if (!selectedArtist || !setlistData) {
      setError('No setlist data available');
      return;
    }

    // Prevent duplicate calls if already creating
    if (addingToLibrary) {
      return;
    }

    // If playlist already exists, open it instead of creating a new one
    if (playlistStatus === 'created' && storedPlaylist) {
      if (storedPlaylist.playlistUrl) {
        window.open(storedPlaylist.playlistUrl, '_blank');
        return;
      }
    }

    setAddingToLibrary(true);
    setError('');
    setSuccess('');

    try {
      // MVP: Call backend API to create Spotify playlist
      const apiUrl = (window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.startsWith('192.168.'))
        ? 'http://localhost:3001' 
        : 'https://setlist-playlist-generator.onrender.com';
      const response = await fetch(`${apiUrl}/api/playlists/create-spotify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          artistName: selectedArtist.name,
          artistMbid: selectedArtist.mbid,
          songs: setlistData.songs,
          playlistName: null // Let backend generate FestPrep format
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create playlist');
      }

      const result = await response.json();
      
      // Store playlist in cache
      const hash = generatePlaylistHash(selectedArtist.mbid, setlistData);
      const newPlaylist: StoredPlaylist = {
        playlistId: result.playlistId,
        playlistUrl: result.playlistUrl,
        shortUrl: result.shortUrl,
        platform: 'spotify',
        artistName: selectedArtist.name,
        playlistName: result.playlistName,
        createdAt: Date.now()
      };
      
      PlaylistCache.store(hash, newPlaylist);
      setStoredPlaylist(newPlaylist);
      setPlaylistStatus('created');

      // Track playlist created
      trackPlaylist({
        artistName: selectedArtist.name,
        artistId: selectedArtist.mbid,
        songCount: setlistData.songs.length,
        platform: 'spotify',
        playlistId: result.playlistId,
        action: 'created'
      });

      // Show confirmation banner for 2-3 seconds
      setShowConfirmationBanner(true);
      setTimeout(() => setShowConfirmationBanner(false), 2500);

      // Note: For MVP, we don't auto-open mock playlist URLs
      // In production with real Spotify auth, this would open the actual playlist
      logger.log('Playlist created:', result.playlistUrl);
      
    } catch (error) {
      logger.error('Failed to create playlist:', error);
      setError('Failed to create playlist. Please try again.');

      // Track playlist creation failure
      trackPlaylistCreationFailed(
        selectedArtist.name,
        error instanceof Error ? error.message : 'Unknown error'
      );
    } finally {
      setAddingToLibrary(false);
    }
  };

  const renderTabContent = () => {
    // Two-panel layout with reordered components
    return (
          <div className="space-y-4 sm:space-y-6">
            <div className="max-w-full mx-auto px-0 sm:px-0">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 lg:p-8" style={{maxWidth: '800px', margin: '0 auto'}}>
                
                {/* FestPrep Header - Only show when no artist is selected */}
                {!selectedArtist && (
                  <div className="text-center mb-8">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900">
                      FestPrep
                    </h1>
                    <p className="text-lg sm:text-xl text-gray-600 mt-2">
                      Generate setlist playlists for any artist or festival
                    </p>
                  </div>
                )}
                
                {/* Song List Section (appears above search when artist is selected) */}
                {selectedArtist && (
                  <div className="mb-8 sm:mb-8 border-b border-gray-300 pb-6 sm:pb-4">
                    {loadingSetlist || !setlistData ? (
                      <>
                        {/* Artist Info with Background Image during loading */}
                        <div className="mb-4">
                          <div 
                            className="relative rounded-lg overflow-hidden"
                            style={{
                              backgroundImage: (selectedArtist as Artist).artwork?.url 
                                ? `url(${(selectedArtist as Artist).artwork!.url})` 
                                : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center 30%',
                              minHeight: '120px'
                            }}
                          >
                            {/* Overlay for better text readability */}
                            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent"></div>
                            
                            {/* Content over background */}
                            <div className="relative z-10 p-4 sm:p-6 flex flex-col justify-between h-full min-h-[120px]">
                              <div className="flex items-center min-w-0 flex-1 mb-4">
                                <h3 className={`font-bold text-white leading-none whitespace-nowrap text-shadow-lg ${
                                  (selectedArtist as Artist).name.length > 25 
                                    ? 'text-lg sm:text-xl' 
                                    : (selectedArtist as Artist).name.length > 20 
                                      ? 'text-xl sm:text-2xl' 
                                      : (selectedArtist as Artist).name.length > 16 
                                        ? 'text-2xl sm:text-3xl' 
                                        : (selectedArtist as Artist).name.length > 12 
                                          ? 'text-3xl sm:text-4xl'
                                          : 'text-4xl sm:text-5xl'
                                }`} style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                                  {(selectedArtist as Artist).name}
                                </h3>
                              </div>
                            </div>
                            
                            {/* Fallback for no image */}
                            {!(selectedArtist as Artist).artwork?.url && (
                              <div className="absolute inset-0 bg-gradient-to-r from-gray-600 to-gray-500"></div>
                            )}
                          </div>
                        </div>

                        {/* Informational Text - Shows while loading */}
                        <div className="mt-2 mb-3 text-center">
                          <p className="text-gray-700 text-sm leading-relaxed">
                            Average setlist based on setlist.fm data.
                            <br className="mb-2" />
                            Click "Create Playlist" to generate a public Spotify playlist to save to your library.
                          </p>
                        </div>

                        {/* Loading indicator */}
                        <div className="flex items-center justify-center py-12 sm:py-8">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 sm:h-6 sm:w-6 border-b-2 border-teal-600 mb-4 mx-auto"></div>
                            <p className="text-gray-600 text-base sm:text-sm">Loading setlist for {(selectedArtist as Artist).name}...</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div>
                        {/* Artist Info with Background Image and Action Buttons */}
                        <div className="mb-4">
                          <div 
                            className="relative rounded-lg overflow-hidden"
                            style={{
                              backgroundImage: (selectedArtist as Artist).artwork?.url 
                                ? `url(${(selectedArtist as Artist).artwork!.url})` 
                                : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center 30%',
                              minHeight: '120px'
                            }}
                          >
                            {/* Overlay for better text readability */}
                            <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent"></div>
                            
                            {/* Content over background */}
                            <div className="relative z-10 p-4 sm:p-6 flex flex-col justify-between h-full min-h-[120px]">
                              <div className="flex items-center min-w-0 flex-1 mb-4">
                                <h3 className={`font-bold text-white leading-none whitespace-nowrap text-shadow-lg ${
                                  (selectedArtist as Artist).name.length > 25 
                                    ? 'text-lg sm:text-xl' 
                                    : (selectedArtist as Artist).name.length > 20 
                                      ? 'text-xl sm:text-2xl' 
                                      : (selectedArtist as Artist).name.length > 16 
                                        ? 'text-2xl sm:text-3xl' 
                                        : (selectedArtist as Artist).name.length > 12 
                                          ? 'text-3xl sm:text-4xl'
                                          : 'text-4xl sm:text-5xl'
                                }`} style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                                  {(selectedArtist as Artist).name}
                                </h3>
                              </div>
                              <div className="flex items-center gap-2 sm:gap-3 justify-start">
                                <button 
                                  onClick={playlistStatus === 'created' && storedPlaylist?.playlistUrl ? 
                                    () => window.open(storedPlaylist.playlistUrl, '_blank') : 
                                    handleAddToLibrary
                                  }
                                  disabled={addingToLibrary}
                                  className={`${
                                    playlistStatus === 'created' 
                                      ? 'bg-green-600 hover:bg-green-700' 
                                      : 'bg-teal-600 hover:bg-teal-700'
                                  } disabled:bg-teal-400 disabled:cursor-not-allowed text-white px-3 py-2 sm:px-5 sm:py-2.5 rounded text-sm sm:text-lg font-medium flex items-center gap-2 transition-colors flex-shrink-0 shadow-lg backdrop-blur-sm`}
                                >
                                  {addingToLibrary ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  ) : playlistStatus === 'created' ? (
                                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.84-.179-.959-.571-.12-.389.18-.78.571-.9 4.56-1.021 8.52-.6 11.64 1.32.36.18.48.659.33 1.052zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                  )}
                                  <span className="whitespace-nowrap">
                                    {addingToLibrary ? 'Creating...' : playlistStatus === 'created' ? 'Open in Spotify' : 'Create Playlist'}
                                  </span>
                                </button>
                                
                                {/* Only show share button after playlist is created */}
                                {playlistStatus === 'created' && (
                                  <button 
                                    onClick={() => setShowShareModal(true)}
                                    className="bg-white/20 hover:bg-white/30 text-white p-2 sm:p-2.5 rounded text-lg font-medium flex items-center justify-center transition-colors flex-shrink-0 aspect-square h-10 sm:h-12 backdrop-blur-sm shadow-lg"
                                    title="Share setlist"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0-12l-3 3m3-3l3 3" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {/* Fallback for no image */}
                            {!(selectedArtist as Artist).artwork?.url && (
                              <div className="absolute inset-0 bg-gradient-to-r from-gray-600 to-gray-500"></div>
                            )}
                          </div>
                        </div>

                        {/* Error Messages - Mobile-First */}
                        {error && (
                          <div className="mb-6 sm:mb-4">
                            <div className="bg-red-50 border border-red-200 rounded p-4 sm:p-3 text-sm sm:text-sm">
                              <span className="text-red-500 mr-2">⚠️</span>
                              <span className="text-red-700">{error}</span>
                            </div>
                          </div>
                        )}

                        {/* Informational Text - Between Artist Card and Song Table */}
                        {playlistStatus !== 'created' && (
                          <div className="mt-2 mb-3 text-center">
                            <p className="text-gray-700 text-sm leading-relaxed">
                              Average setlist based on setlist.fm data.
                              <br className="mb-2" />
                              Click{' '}
                              <button
                                onClick={handleAddToLibrary}
                                disabled={addingToLibrary}
                                className="text-teal-600 hover:text-teal-700 underline font-medium disabled:text-teal-400 disabled:cursor-not-allowed"
                              >
                                "Create Playlist"
                              </button>
                              {' '}to generate a public Spotify playlist to save to your library.
                            </p>
                          </div>
                        )}

                        {/* Horizontal Rule */}
                        <hr className="my-6 border-gray-300" />

                        {/* Song List */}
                        <div className="mt-4">
                          <div className="space-y-2">
                            {setlistData.songs.map((song, index) => (
                              <div
                                key={`${song.name}-${index}-${song.appleMusicTrack?.duration || 'noduration'}`}
                                className="text-center"
                              >
                                <div className="min-w-0">
                                  <div className={`text-sm leading-tight ${
                                    song.found === false ? 'text-gray-400' : 'text-gray-900'
                                  }`}>
                                    {song.appleMusicTrack?.name || song.name}
                                  </div>
                                  {(song.found === false || (song.isCover && song.coverArtist)) && (
                                    <div className="flex items-center justify-center gap-2 mt-1">
                                      {song.found === false && (
                                        <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded">
                                          Unavailable
                                        </span>
                                      )}
                                      {song.isCover && song.coverArtist && (
                                        <span className="text-xs text-gray-500">
                                          {song.coverArtist} • Cover
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                )}

                {/* Confirmation Banner */}
                {showConfirmationBanner && (
                  <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🎉</span>
                      <span className="font-medium">Playlist Ready!</span>
                    </div>
                  </div>
                )}

                {/* Search Section */}
                {!selectedArtist && (
                  <div className="mb-8">
                    <div className="mx-auto">
                      <p className="text-gray-500 text-xs text-left mb-2">
                        FestPrep will generate a playlist based on Setlist.fm data. Add or Copy it to your library and enjoy the show!
                      </p>
                      <MockArtistAutocomplete
                        value={artistName}
                        onChange={setArtistName}
                        onSelect={handleArtistSelect}
                        placeholder="Search for an artist or festival..."
                        className="w-full"
                      />
                      <p className="text-red-500 text-sm mt-3 text-center">*Festivals search coming soon</p>
                    </div>
                  </div>
                )}
                
                {/* Search when artist is selected */}
                {selectedArtist && (
                  <div className="mb-8 sm:mb-8">
                    <MockArtistAutocomplete
                      value={artistName}
                      onChange={setArtistName}
                      onSelect={handleArtistSelect}
                      placeholder="Search for an artist or festival..."
                      className="w-full"
                    />
                    <p className="text-red-500 text-xs mt-2 text-center">*Festivals search coming soon</p>
                  </div>
                )}

              </div>
            </div>
          </div>
    );
  };

  return (
    <div className="min-h-screen relative flex flex-col">
      <BackgroundPattern />
      
      <main className="flex-1 overflow-y-auto p-2 sm:p-4 relative z-10">
        <div className="mx-auto">
          {renderTabContent()}
        </div>
      </main>
      
      {/* Footer */}
      <div style={{ 
        backgroundColor: '#1f2937', 
        color: 'white', 
        padding: '20px', 
        textAlign: 'center',
        position: 'relative',
        zIndex: 1000,
        display: 'block',
        width: '100%',
        minHeight: '80px'
      }}>
        <p style={{margin: '0 0 10px 0', fontSize: '14px'}}>
          Contact: <a href="mailto:contact@festprep.com" style={{color: '#60a5fa'}}>contact@festprep.com</a>
        </p>
        <p style={{margin: '0', fontSize: '14px'}}>
          Powered by <a href="https://www.setlist.fm/" style={{color: '#60a5fa'}}>Setlist.fm</a> • 
          Patterns by <a href="https://www.vecteezy.com/" style={{color: '#60a5fa'}}>Vecteezy.com</a>
        </p>
      </div>

      {/* Legacy modal player removed - using bottom player instead */}


      {showShareModal && selectedArtist && setlistData && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          playlistUrl={storedPlaylist?.playlistUrl}
          shortUrl={storedPlaylist?.shortUrl}
          artistName={selectedArtist.name}
          songs={setlistData.songs.map(song => song.appleMusicTrack?.name || song.name)}
        />
      )}
    </div>
  );
};

export default Home;
